/* eslint @typescript-eslint/no-unused-vars: 0 */

import axios from 'axios';
import { Candidate } from '../../components/search/Candidate';
import { Lang } from '../../states/query';
import { convertEWSToLocation, GeoLocation } from '../../utils/geoLocation';
import { Point } from '../ekispert/models';
import { GetFlightInfoRequest, RequestDiaList, RequestSearchCourse } from './models/searchCourse';
import Environment from '../../utils/environment';
import { GetFlightInfoResponse } from './models/aswLinkageInfo';
import { InterruptToTransferRequest } from './models/interruptToTransfer';


export interface ApiConfig {
  baseURL: string;
  timeout: number;
}

const TIMEOUT_LIMIT = 40000;

// mockのURLを貼る
const MOCK_API_CONFIG: ApiConfig = {
  baseURL: '/',
  timeout: TIMEOUT_LIMIT,
};

// HTTP STATUS TEST用
const HTTP_STAT_CONFIG: ApiConfig = {
  baseURL: 'https://httpstat.us/',
  timeout: TIMEOUT_LIMIT,
};

export const API_DEV_CONFIG: ApiConfig = {
  baseURL: 'https://ana-maas-api-dev.val.jp/',
  timeout: TIMEOUT_LIMIT,
}

export const API_STAGE_CONFIG: ApiConfig = {
  baseURL: 'https://ana-maas-api-stg.val.jp/',
  timeout: TIMEOUT_LIMIT,
}

export const API_PROD_CONFIG: ApiConfig = {
  baseURL: 'https://ana-maas-api.val.jp/',
  timeout: TIMEOUT_LIMIT,
}

/**
 * StatusErrorCodeテスト用に、任意のHTTPStatusCodeを返すモックエンドポイントにリクエストを投げる
 * @param statusCode
 * @returns
 */
const getErrorStatus = async (statusCode: number) => {
  const useAPIConfig: ApiConfig = HTTP_STAT_CONFIG;
  const instance = axios.create(useAPIConfig);
  return await instance.get(`/${statusCode}`);
}

/** テスト用のバックエンドを使用するかを判別する */
/** Localから本番用のエンドポイントを使う場合には常に「API_PROD_CONFIG」を返すように変更してください */
export const getUseAPI = (): ApiConfig => {
  /** 実行環境を判別するための環境変数。 */
  const env = process.env['REACT_APP_STAGE_ENV'] || '';
  if(Environment.isProduction(env)) {
    return API_PROD_CONFIG;
  }
  switch(Environment.isBackendEnv()) {
    case Environment.PRODUCTION:
      return API_PROD_CONFIG;
    case Environment.STAGING:
      return API_STAGE_CONFIG;
    case Environment.DEV:
      return API_DEV_CONFIG;
    default:
      return API_STAGE_CONFIG;
  }
}

/**
 * 駅すぱあとから返されるインクリメンタルサーチの候補のうち、「福岡」など分かりにくい駅名は置換をする
 * @param points 
 * @returns 
 */
const replaceStationName = (points: Point[] | Point) => {
  const replaceOneStationName = (point: Point) => {
    // 置き換え対象の駅リスト
    const replaceStationList = [{
      point: "24761",
      replaceName: '福岡（富山県）'
    }];
    // 対応駅のうち、PointList
    const replaceStationPointList = replaceStationList.map((station)=>{
      return station.point;
    });
    if(replaceStationPointList.includes(point.Station.code)) {
      const replacePoint = replaceStationList.find((station)=>{
        return station.point === point.Station.code
      });
      // インクリメンタルサーチに表示される駅名の置き換えをする
      if(replacePoint) {
        point.Station.Name = replacePoint.replaceName;
      }
    }
    return point;
  }
  if(Array.isArray(points)) {
    return points.map((point)=>{
      return replaceOneStationName(point);
    })
  }
  else {
    return replaceOneStationName(points);
  }
}

/**
 * 駅詳細情報を取得する
 * （広告の出発・到着駅の緯度経度を取得するため）
 * codeとname両方とも入れられた場合にはnameを優先する
 * /station/
 * @param code 取得する駅コード
 * @param name テキストボックスで入力された値。
 * @param optionConfig 独自で指定したAPIの設定など（任意）
 */
export async function getStationDetail(code: string | null, name: string | null, lang: Lang = Lang.ja, optionConfig?: object) {
  // 使用するAPI
  const useAPIConfig: ApiConfig = getUseAPI();
  const config = {
    ...useAPIConfig,
    ...optionConfig,
  };
  axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
  const instance = axios.create(config);

  interface StationDetailParams {
    code?: string,
    name?: string,
    language: Lang,
    type: string,
    gcs: string
  }
  const params: StationDetailParams = {
    language: lang,
    type: 'train:ship:bus',
    gcs: 'wgs84'
  };
  if(code) {
    params.code = code
  }
  if(name) {
    params.name = name
  }
  // あとで見直す（2019/12/23現在）
  // eslint-disable-next-line no-useless-catch
  const response = await instance.get(`fetch-station`, { params });

  if (response.status !== 200) {
    throw new Error('Server Error');
  }

  // 取得したレスポンスがundefinedではない場合に、レスポンスの値を代入する。
  if (typeof response.data.ResultSet.Point !== undefined && response.data.ResultSet.Point !== undefined) {
    return replaceStationName(response.data.ResultSet.Point);
  }
  return [];
}

export const changeStationDetailToCandidate = (points: Point[]): Candidate[] => {
  if(!points || !Array.isArray(points)) {
    return [];
  }
  return points.map((p)=>{
    const geoPoint: GeoLocation | null = p.GeoPoint? convertEWSToLocation(p.GeoPoint): null;
    return new Candidate(p.Station.Name, p.Station.code, p.Station.Type, geoPoint, p.Prefecture);
  })
}

/**
 * 駅すぱあと経路検索を行う
 * /search
 * @param conditions 
 */
export const getCourses = async (conditions: RequestSearchCourse) => {
  axios.defaults.headers.post['Content-Type'] = 'application/json';
  // 使用するAPI
  const useAPIConfig: ApiConfig = getUseAPI();
  const instance = axios.create(useAPIConfig);
  const response = await instance.post('/search', conditions, {
    headers: { 
      'Content-Type': 'application/json'
    }
  });
  return response.data;
}

/**
 * 前後の列車・バス・搭乗便一覧を取得する
 * @param conditions
 * @returns
 */
export const getPublicZengoDia = async (conditions: RequestDiaList) => {
  axios.defaults.headers.post['Content-Type'] = 'application/json';
  // 使用するAPI
  const useAPIConfig: ApiConfig = getUseAPI();
  const instance = axios.create(useAPIConfig);
  const response = await instance.post('/search/dia-list', conditions);
  if (response.status !== 200) {
    throw new Error('Server Error');
  }
  return response.data.ResultSet;
}

/**
 * ASWスクレイピング結果のフライト情報を取得する
 * /get-flight-info
 * @param conditions
 * @returns
 */
export const getFlightInfo = async (conditions: GetFlightInfoRequest): Promise<GetFlightInfoResponse> => {
  axios.defaults.headers.post['Content-Type'] = 'application/json';
  // 使用するAPI
  const useAPIConfig: ApiConfig = getUseAPI();
  const instance = axios.create(useAPIConfig);
  const response = await instance.post('/get-flight-info', conditions);

  return response.data;
}

/*
 * 運行情報を取得する
 * /train-info
 * @param conditions
 */
export const getTrainInfo = async () => {
  axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
  // 使用するAPI
  const useAPIConfig: ApiConfig = getUseAPI();
  const instance = axios.create(useAPIConfig);
  const path = '/train-info';
  const response = await instance.get(path);
  if (response.status !== 200) {
    throw new Error('Server Error');
  }
  return response.data.ResultSet;
}

/**
 * 前後のダイヤより再検索をする
 * /search/ekispert/assign-dia
 * @param request 再検索に必要なRequestBody
 */
export const assignDia = async (request: any) => {
  axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
  // 使用するAPI
  const useAPIConfig: ApiConfig = getUseAPI();
  const instance = axios.create(useAPIConfig);
  const path = '/search/ekispert/assign-dia';
  const response = await instance.post(path, request, {
    headers: {
      'Content-Type': 'application/json'
    }
  });
  if (response.status !== 200) {
    throw new Error('Server Error');
  }
  return response.data;
}

/**
 * 回避する機能を実行する
 * /search/interrupt-to-transfer
 * @param request 回避機能実行に必要なRequestBody
 */
export const interruptToTransfer = async (request: InterruptToTransferRequest) => {
  axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
  // 使用するAPI
  const useAPIConfig: ApiConfig = getUseAPI();
  const instance = axios.create(useAPIConfig);
  const path = '/search/interrupt-to-transfer';
  const response = await instance.post(path, request, {
    headers: {
      'Content-Type': 'application/json'
    }
  });
  if (response.status !== 200) {
    throw new Error('Server Error');
  }
  return response.data;
}
